/**
 * useFormModal - React Hook Form Integration for EnhancedFormModal
 *
 * Provides seamless integration between react-hook-form and EnhancedFormModal.
 * Handles form state, validation, dirty check, and submission.
 *
 * Features:
 * - Zod schema validation
 * - Automatic dirty state tracking
 * - Submit handling with loading state
 * - Error handling and persistence
 * - TypeScript type safety
 *
 * @module use-form-modal
 */

import { useForm, type UseFormProps, type UseFormReturn, type FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import * as React from 'react';

import { logger } from '../../../utils';

// ============================================================================
// Types
// ============================================================================

/**
 * useFormModal options
 */
export interface UseFormModalOptions<TFieldValues extends FieldValues = FieldValues> {
  /**
   * Zod schema for validation
   */
  schema?: z.ZodSchema<TFieldValues>;

  /**
   * Default values for the form
   */
  defaultValues?: UseFormProps<TFieldValues>['defaultValues'];

  /**
   * Validation mode
   * @default 'onChange'
   */
  mode?: UseFormProps<TFieldValues>['mode'];

  /**
   * Form submission handler
   */
  onSubmit: (data: TFieldValues) => Promise<void> | void;

  /**
   * Success callback after successful submission
   */
  onSuccess?: () => void;

  /**
   * Error callback when submission fails
   */
  onError?: (error: Error) => void;

  /**
   * Optional persistence key for localStorage
   * If provided, form data will be automatically saved to localStorage
   */
  persistKey?: string;

  /**
   * Persistence duration in milliseconds
   * @default 3600000 (1 hour)
   */
  persistDuration?: number;
}

/**
 * useFormModal return value
 */
export interface UseFormModalReturn<TFieldValues extends FieldValues = FieldValues>
  extends Omit<UseFormReturn<TFieldValues>, 'handleSubmit'> {
  /**
   * Whether form has unsaved changes
   */
  isDirty: boolean;

  /**
   * Whether form is currently submitting
   */
  isSubmitting: boolean;

  /**
   * Form submission handler (use with onSubmit)
   */
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;

  /**
   * Submit error if any
   */
  submitError: Error | null;

  /**
   * Reset form to initial state
   */
  resetForm: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useFormModal - React Hook Form integration for modals
 *
 * Combines react-hook-form with EnhancedFormModal features including
 * validation, dirty check, persistence, and submission handling.
 *
 * @example
 * Basic usage with StandardFormModal
 */
export function useFormModal<TFieldValues extends FieldValues = FieldValues>({
  schema,
  defaultValues,
  mode = 'onChange',
  onSubmit,
  onSuccess,
  onError,
  persistKey,
  persistDuration = 3600000, // 1 hour default
}: UseFormModalOptions<TFieldValues>): UseFormModalReturn<TFieldValues> {
  // ============================================================================
  // State
  // ============================================================================

  const [submitError, setSubmitError] = React.useState<Error | null>(null);
  const [isSubmittingState, setIsSubmittingState] = React.useState(false);

  // ============================================================================
  // Form Setup
  // ============================================================================

  // Load persisted data if available
  const getInitialValues = React.useCallback(() => {
    if (!persistKey) return defaultValues;

    try {
      const stored = localStorage.getItem(persistKey);
      if (!stored) return defaultValues;

      const parsed = JSON.parse(stored);
      const { data, timestamp } = parsed;

      // Check if data has expired
      if (Date.now() - timestamp > persistDuration) {
        localStorage.removeItem(persistKey);
        return defaultValues;
      }

      return data;
    } catch {
      // If parsing fails, return default values
      return defaultValues;
    }
  }, [persistKey, defaultValues, persistDuration]);

  const form = useForm<TFieldValues>({
    defaultValues: getInitialValues(),
    mode,
    // TypeScript workaround: Zod resolver has complex generic constraints
    // Using conditional type resolution for schema validation
    ...(schema
      ? {
          // @ts-expect-error - Type incompatibility between Zod v3 and react-hook-form resolver types, works functionally
          resolver: zodResolver(schema) as unknown as UseFormProps<TFieldValues>['resolver'],
        }
      : {}),
  });

  // ============================================================================
  // Persistence
  // ============================================================================

  // Auto-save form data to localStorage
  React.useEffect(() => {
    if (!persistKey) return;

    const subscription = form.watch((data) => {
      try {
        const payload = {
          data,
          timestamp: Date.now(),
        };
        localStorage.setItem(persistKey, JSON.stringify(payload));
      } catch (error) {
        // Fail silently if localStorage is unavailable
        logger.warn('Failed to persist form data', { persistKey, error });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, persistKey]);

  // ============================================================================
  // Submission
  // ============================================================================

  const handleSubmit = React.useCallback(
    async (e?: React.BaseSyntheticEvent) => {
      if (e) {
        e.preventDefault();
      }

      setSubmitError(null);
      setIsSubmittingState(true);

      try {
        await form.handleSubmit(async (data) => {
          await onSubmit(data);

          // Clear persisted data on successful submission
          if (persistKey) {
            localStorage.removeItem(persistKey);
          }

          onSuccess?.();
        })(e);
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Submission failed');
        setSubmitError(err);
        onError?.(err);
      } finally {
        setIsSubmittingState(false);
      }
    },
    [form, onSubmit, onSuccess, onError, persistKey]
  );

  // ============================================================================
  // Reset
  // ============================================================================

  const resetForm = React.useCallback(() => {
    form.reset();
    setSubmitError(null);

    // Clear persisted data
    if (persistKey) {
      localStorage.removeItem(persistKey);
    }
  }, [form, persistKey]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    ...form,
    isDirty: form.formState.isDirty,
    isSubmitting: isSubmittingState || form.formState.isSubmitting,
    handleSubmit,
    submitError,
    resetForm,
  };
}
